import {
  Badge, Box, Button, Checkbox, Divider, Flex, Input, Modal, ModalBody, ModalCloseButton,
  ModalContent, ModalFooter, ModalHeader, ModalOverlay, Select, Text, useDisclosure,
} from "@chakra-ui/react";
import { useEffect, useState, useReducer, useMemo } from "react";
import { format } from "date-fns";
import { useAuth } from "../../../Hooks/useAuth";
import { api } from "../../../services/api";
import swal from "sweetalert";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const GREEN  = "#004a33";
const ORANGE = "#fc8f04";

function FormField({ label, children }) {
  return (
    <Box mb="14px">
      <Text fontSize="11px" fontWeight="700" color="gray.500" mb="5px" textTransform="uppercase" letterSpacing="0.05em">
        {label}
      </Text>
      {children}
    </Box>
  );
}

function OverviewSection({ title, children }) {
  return (
    <Box mb="18px">
      <Text fontSize="10px" fontWeight="800" color="gray.400" textTransform="uppercase" letterSpacing="0.07em" mb="8px">
        {title}
      </Text>
      {children}
    </Box>
  );
}

function InfoRow({ label, value }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <Flex justify="space-between" align="center" py="4px" gap="10px">
      <Text fontSize="11px" color="gray.400" fontWeight="600">{label}</Text>
      <Text fontSize="12px" color="gray.700" fontWeight="700" textAlign="right" noOfLines={1}>{value}</Text>
    </Flex>
  );
}

function batchStatus(batch) {
  const today = new Date();
  const start = new Date(batch.start_date);
  const end = new Date(batch.expiration_date);
  if (end < today) return { label: "Expirado", color: "#e74c3c", bg: "rgba(231,76,60,.1)" };
  if (start > today) return { label: "Agendado", color: "#2980b9", bg: "rgba(41,128,185,.1)" };
  return { label: "Ativo", color: "#27ae60", bg: "rgba(39,174,96,.1)" };
}

const fmtDate = (d) => {
  try { return format(new Date(d), "dd/MM/yyyy"); } catch { return "—"; }
};

const inputProps = {
  borderRadius: "8px",
  fontSize: "14px",
  bg: "white",
  border: "1px solid rgba(0,74,51,.2)",
  _focus: { borderColor: GREEN, boxShadow: `0 0 0 1px ${GREEN}` },
};

const quillModules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ color: ["black", "red", "blue", "white", "green", "yellow", "#D44120", "#216a0f"] }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

export function EditProduct({
  productId, allProductTypes, allCategories, allSubcategories, allDrinkCategories,
  allBatches, allPrices, onUpdateProduct, thisProduct,
}) {
  const { user } = useAuth();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [productTypes] = useState(allProductTypes);
  const drinkCategories = allDrinkCategories || [];
  const [product, setProduct] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    { name: "", description: "", brand: "", quantity_per_box: "", product_type_id: "", market_price: "", size: "", suggested_price_for_sale: "", customer_review: "", unit_volume_ml: "", is_official_kit: false, event_drink_category: "" }
  );
  const [productPhotos, setProductPhotos] = useState([]);
  const [productFlyer, setProductFlyer] = useState(null);

  useEffect(() => { setProduct(thisProduct); }, [thisProduct]);

  const handleChange = (name, value) => { setProduct({ [name]: value }); };

  // Full overview panel data — reads live from the `product` state (not just
  // `thisProduct`) so switching the "Tipo de bebida" select updates the
  // Categoria/Subcategoria/Calculadora preview immediately, before saving.
  const productType = useMemo(
    () => (allProductTypes || []).find(pt => String(pt.id) === String(product.product_type_id)),
    [allProductTypes, product.product_type_id]
  );
  const category = useMemo(
    () => (allCategories || []).find(c => String(c.id) === String(productType?.category_id)),
    [allCategories, productType]
  );
  const subcategory = useMemo(
    () => (allSubcategories || []).find(s => String(s.id) === String(category?.subcategory_id)),
    [allSubcategories, category]
  );
  const calculatorKey = product.event_drink_category || productType?.event_drink_category || null;
  const calculatorSource = product.event_drink_category ? "produto" : productType?.event_drink_category ? "tipo" : null;
  const calculatorLabel = drinkCategories.find(dc => dc.key === calculatorKey)?.label || calculatorKey;

  const productBatches = useMemo(
    () =>
      (allBatches || [])
        .filter(b => b.product_id === productId)
        .sort((a, b) => new Date(b.start_date) - new Date(a.start_date)),
    [allBatches, productId]
  );

  async function handleSave(e) {
    e.preventDefault();
    const formData = new FormData();
    formData.append("product[name]", product.name);
    formData.append("product[product_type_id]", product.product_type_id);
    formData.append("product[brand]", product.brand);
    formData.append("product[size]", product.size);
    formData.append("product[description]", product.description);
    formData.append("product[market_price]", product.market_price);
    formData.append("product[quantity_per_box]", product.quantity_per_box);
    formData.append("product[suggested_price_for_sale]", product.suggested_price_for_sale);
    formData.append("product[customer_review]", product.customer_review);
    if (product.unit_volume_ml) formData.append("product[unit_volume_ml]", product.unit_volume_ml);
    formData.append("product[is_official_kit]", !!product.is_official_kit);
    formData.append("product[event_drink_category]", product.event_drink_category || "");
    if (productPhotos) {
      for (let i = 0; i < productPhotos.length; i++) {
        formData.append("product[photos][]", productPhotos[i]);
      }
    }
    if (productFlyer) {
      formData.append("product[flyer]", productFlyer);
    }
    const response = await api.put(`/api/v1/products/${productId}`, formData, {
      headers: { "access-token": user.token, client: user.client, uid: user.uid, accept: "application/json", contentType: "multipart/form-data" },
    });
    if (response.status === 200) {
      onUpdateProduct(response.data);
      swal({ title: "Atualizado!", text: "Produto atualizado com sucesso.", icon: "success" });
      onClose();
    } else {
      swal({ title: "Erro", text: "Algo deu errado.", icon: "error" });
      onClose();
    }
  }

  return (
    <>
      <Button
        size="xs" borderRadius="7px" px="10px" h="26px"
        bg={GREEN} color={ORANGE} fontWeight="700" fontSize="11px"
        _hover={{ opacity: 0.85 }} onClick={onOpen}
      >
        Editar
      </Button>

      <Modal isOpen={isOpen} onClose={onClose} isCentered size="6xl" scrollBehavior="inside">
        <ModalOverlay bg="rgba(0,0,0,.45)" backdropFilter="blur(2px)" />
        <ModalContent borderRadius="16px" overflow="hidden" mx="16px" maxH="88vh">
          <ModalHeader bg={GREEN} py="14px" px="20px" pr="44px">
            <Text fontSize="15px" fontWeight="800" color={ORANGE}>Editar Produto</Text>
            <Text fontSize="11px" color="rgba(255,255,255,.45)" fontWeight="500" mt="1px" noOfLines={1}>
              {product.name || `#${productId}`}
            </Text>
          </ModalHeader>
          <ModalCloseButton color="rgba(255,255,255,.6)" _hover={{ color: "white", bg: "transparent" }} top="12px" right="14px" />
          <ModalBody p="0" bg="#fafbfc">
          <Flex direction={{ base: "column", lg: "row" }} align="stretch">
            {/* Overview panel — read-only snapshot of everything related to
                this product on the platform, kept live as the form fields
                (Tipo/categoria da calculadora) change, so the admin sees the
                effect before saving. */}
            <Box
              w={{ base: "100%", lg: "320px" }}
              flexShrink={0}
              bg="white"
              borderRight={{ base: "none", lg: "1px solid rgba(0,74,51,.08)" }}
              borderBottom={{ base: "1px solid rgba(0,74,51,.08)", lg: "none" }}
              p="18px"
              maxH={{ base: "none", lg: "calc(88vh - 130px)" }}
              overflowY={{ base: "visible", lg: "auto" }}
            >
              <Text fontSize="12px" fontWeight="800" color={GREEN} mb="14px">
                Visão geral na plataforma
              </Text>

              <OverviewSection title="Identificação">
                <InfoRow label="ID" value={`#${productId}`} />
                <InfoRow label="Nome" value={product.name} />
                <InfoRow label="Marca" value={product.brand} />
                <InfoRow label="Tamanho" value={product.size} />
                <InfoRow label="Qtd/caixa" value={product.quantity_per_box} />
                <InfoRow label="Volume (ml)" value={product.unit_volume_ml} />
                {!!product.is_official_kit && (
                  <Badge mt="6px" fontSize="9px" borderRadius="4px" bg="rgba(252,143,4,.15)" color="#b56500">
                    Kit oficial Boozen
                  </Badge>
                )}
              </OverviewSection>

              <Divider borderColor="rgba(0,74,51,.08)" mb="14px" />

              <OverviewSection title="Classificação">
                <InfoRow label="Tipo" value={productType?.name || "—"} />
                <InfoRow label="Categoria" value={category?.name || "—"} />
                <InfoRow label="Subcategoria" value={subcategory?.name || "—"} />
              </OverviewSection>

              <Divider borderColor="rgba(0,74,51,.08)" mb="14px" />

              <OverviewSection title="Calculadora de eventos">
                {calculatorKey ? (
                  <Box>
                    <Text
                      fontSize="10px" fontWeight="700" color={GREEN} bg="rgba(0,74,51,.08)"
                      borderRadius="99px" px="8px" py="2px" textTransform="uppercase" letterSpacing="0.03em"
                      display="inline-block"
                    >
                      {calculatorLabel}
                    </Text>
                    <Text fontSize="10px" color="gray.400" mt="4px">
                      {calculatorSource === "produto" ? "Definida neste produto" : "Herdada do Tipo de produto"}
                    </Text>
                  </Box>
                ) : (
                  <Text fontSize="11px" color="gray.400">Não aparece na calculadora</Text>
                )}
              </OverviewSection>

              <Divider borderColor="rgba(0,74,51,.08)" mb="14px" />

              <OverviewSection title="Preços de referência">
                <InfoRow
                  label="Mercado"
                  value={product.market_price ? Number(product.market_price).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : "—"}
                />
                <InfoRow
                  label="Sugerido"
                  value={product.suggested_price_for_sale ? Number(product.suggested_price_for_sale).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : "—"}
                />
              </OverviewSection>

              <Divider borderColor="rgba(0,74,51,.08)" mb="14px" />

              <OverviewSection title={`Lotes (${productBatches.length})`}>
                {productBatches.length === 0 ? (
                  <Text fontSize="11px" color="gray.400">Nenhum lote cadastrado para este produto.</Text>
                ) : (
                  <Box display="flex" flexDirection="column" gap="10px">
                    {productBatches.map(batch => {
                      const status = batchStatus(batch);
                      const batchPrices = (allPrices || [])
                        .filter(pr => pr.batch_id === batch.id)
                        .sort((a, b) => a.unity - b.unity);
                      return (
                        <Box key={batch.id} bg="rgba(0,74,51,.03)" borderRadius="8px" p="10px" border="1px solid rgba(0,74,51,.06)">
                          <Flex justify="space-between" align="center" mb="4px">
                            <Text fontSize="11px" fontWeight="700" color="gray.600">Lote #{batch.id}</Text>
                            <Badge fontSize="9px" borderRadius="4px" bg={status.bg} color={status.color}>
                              {status.label}
                            </Badge>
                          </Flex>
                          <InfoRow label="Estoque" value={`${batch.stock} ${batch.unity_of_measurement || ""}`} />
                          <InfoRow label="Desconto" value={batch.discount_per_centage ? `${batch.discount_per_centage}%` : "—"} />
                          <InfoRow label="Início" value={fmtDate(batch.start_date)} />
                          <InfoRow label="Expiração" value={fmtDate(batch.expiration_date)} />
                          {batchPrices.length > 0 && (
                            <Box mt="6px" pt="6px" borderTop="1px solid rgba(0,74,51,.08)">
                              {batchPrices.map(pr => (
                                <InfoRow
                                  key={pr.id}
                                  label={`Até ${pr.unity} ${batch.unity_of_measurement || "unid."}${pr.unity > 1 ? "s" : ""}`}
                                  value={Number(pr.price_per_unity).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                                />
                              ))}
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </OverviewSection>
            </Box>

            {/* Form */}
            <Box flex="1" p="20px" maxH={{ base: "none", lg: "calc(88vh - 130px)" }} overflowY={{ base: "visible", lg: "auto" }}>
            <FormField label="Nome">
              <Input placeholder={product.name} name="name" onChange={e => handleChange("name", e.target.value)} {...inputProps} />
            </FormField>
            <Flex gap="10px">
              <Box flex="2">
                <FormField label="Marca">
                  <Input placeholder={product.brand} name="brand" onChange={e => handleChange("brand", e.target.value)} {...inputProps} />
                </FormField>
              </Box>
              <Box flex="1">
                <FormField label="Tamanho">
                  <Input placeholder={product.size} name="size" onChange={e => handleChange("size", e.target.value)} {...inputProps} />
                </FormField>
              </Box>
              <Box flex="1">
                <FormField label="Qtd/caixa">
                  <Input placeholder={String(product.quantity_per_box)} type="number" name="quantity_per_box" onChange={e => handleChange("quantity_per_box", e.target.value)} {...inputProps} />
                </FormField>
              </Box>
            </Flex>
            <FormField label="Tipo de bebida">
              <Select name="product_type_id" onChange={e => handleChange("product_type_id", e.target.value)} {...inputProps}>
                <option value="">Selecione o tipo</option>
                {productTypes.map(pt => (
                  <option key={pt.id} value={pt.id}>{pt.name}</option>
                ))}
              </Select>
            </FormField>
            <Flex gap="10px">
              <Box flex="1">
                <FormField label="Preço de mercado (R$)">
                  <Input placeholder={String(product.market_price || "")} type="number" name="market_price" onChange={e => handleChange("market_price", e.target.value)} {...inputProps} />
                </FormField>
              </Box>
              <Box flex="1">
                <FormField label="Preço sugerido (R$)">
                  <Input placeholder={String(product.suggested_price_for_sale || "")} type="number" name="suggested_price_for_sale" onChange={e => handleChange("suggested_price_for_sale", e.target.value)} {...inputProps} />
                </FormField>
              </Box>
            </Flex>
            <Box maxW="260px">
              <FormField label="Volume (ml) — barril ou garrafa">
                <Input placeholder={String(product.unit_volume_ml || "ex: 20000 ou 750")} type="number" name="unit_volume_ml" onChange={e => handleChange("unit_volume_ml", e.target.value)} {...inputProps} />
              </FormField>
              <Text fontSize="11px" color="gray.400" mt="4px">
                Usado pela calculadora de eventos: volume do barril (chope/drink em barril) ou da garrafa (cerveja, gin, vodka, cachaça).
              </Text>
            </Box>
            <Box mb="14px" maxW="360px">
              <FormField label="Categoria da calculadora (só este produto)">
                <Select value={product.event_drink_category || ""} name="event_drink_category" onChange={e => handleChange("event_drink_category", e.target.value)} {...inputProps}>
                  <option value="">— Usar a categoria do Tipo de Produto —</option>
                  {drinkCategories.map(dc => (
                    <option key={dc.key} value={dc.key}>{dc.label}</option>
                  ))}
                </Select>
              </FormField>
              <Text fontSize="11px" color="gray.400" mt="4px">
                Só preencha se ESTE produto precisa de uma categoria diferente do Tipo dele — hoje o uso real é marcar só os
                2-3 produtos de Gin/Vodka/Cachaça/Licor escolhidos pela Boozen, deixando o Tipo sem categoria.
              </Text>
            </Box>
            <Box mb="14px">
              <Checkbox colorScheme="green" isChecked={!!product.is_official_kit} onChange={e => handleChange("is_official_kit", e.target.checked)}>
                <Text fontSize="13px" fontWeight="600">Kit oficial Boozen</Text>
              </Checkbox>
              <Text fontSize="11px" color="gray.400" mt="4px">Produtos marcados aparecem primeiro nas sugestões da calculadora de eventos.</Text>
            </Box>
            <FormField label="Descrição">
              <ReactQuill theme="snow" modules={quillModules} placeholder="Descrição do produto" onChange={v => handleChange("description", v)} style={{ background: "white", borderRadius: "8px" }} />
            </FormField>
            <FormField label="Review do cliente">
              <ReactQuill theme="snow" modules={quillModules} placeholder="Review para exibir ao cliente" onChange={v => handleChange("customer_review", v)} style={{ background: "white", borderRadius: "8px" }} />
            </FormField>
            <Flex gap="12px" mt="4px">
              <Box>
                <Input type="file" id={`editPhotos-${productId}`} display="none" multiple name="photos" onChange={e => setProductPhotos(e.target.files)} />
                <label htmlFor={`editPhotos-${productId}`}>
                  <Box
                    as="span" display="inline-flex" alignItems="center" gap="6px"
                    border={`1px solid ${GREEN}`} borderRadius="8px" px="12px" py="7px"
                    cursor="pointer" fontSize="12px" fontWeight="700" color={GREEN}
                    _hover={{ bg: GREEN, color: ORANGE }}
                  >
                    Fotos {productPhotos?.length > 0 ? `(${productPhotos.length})` : ""}
                  </Box>
                </label>
              </Box>
              <Box>
                <Input type="file" id={`editFlyer-${productId}`} display="none" multiple={false} name="flyer" onChange={e => setProductFlyer(e.target.files[0])} />
                <label htmlFor={`editFlyer-${productId}`}>
                  <Box
                    as="span" display="inline-flex" alignItems="center" gap="6px"
                    border={`1px solid ${GREEN}`} borderRadius="8px" px="12px" py="7px"
                    cursor="pointer" fontSize="12px" fontWeight="700" color={GREEN}
                    _hover={{ bg: GREEN, color: ORANGE }}
                  >
                    Flyer {productFlyer ? `(${productFlyer.name.substring(0, 12)}…)` : ""}
                  </Box>
                </label>
              </Box>
            </Flex>
            </Box>
          </Flex>
          </ModalBody>
          <ModalFooter bg="white" borderTop="1px solid rgba(0,74,51,.08)" px="20px" py="12px" gap="8px" justifyContent="flex-end">
            <Button size="sm" variant="ghost" colorScheme="gray" borderRadius="8px" onClick={onClose}>Cancelar</Button>
            <Button size="sm" bg={GREEN} color={ORANGE} borderRadius="8px" px="16px" fontWeight="700" fontSize="12px" _hover={{ opacity: 0.88 }} onClick={handleSave}>
              Salvar alterações
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
